/**
 * BackgroundEffects — Particle, glow, matrix, biohazard, DNA effects for landing page
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

let backgroundEffectsEnabled = true;
let mouseTrailEnabled = true;
let lastTrailTime = 0;
let energyRingInterval = null;

const NEON_COLORS = ['#ff00ff', '#00ffff', '#00ff88', '#aa00ff', '#ff0044', '#0088ff'];

// =========================================================================
// PARTICLE GENERATORS
// =========================================================================

function createBackgroundParticles(className, count, opts = {}) {
    const container = document.getElementById('virus-particles');
    if (!container) return;
    const { minSize = 10, maxSize = 30, minDur = 20, maxDur = 35 } = opts;
    for (let i = 0; i < count; i++) {
        const el = document.createElement('div');
        el.className = className;
        const size = Math.random() * (maxSize - minSize) + minSize;
        const color = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
        const left = Math.random() * 100;
        const duration = Math.random() * (maxDur - minDur) + minDur;
        const delay = Math.random() * duration;
        el.style.cssText = `width:${size}px;height:${size}px;left:${left}%;color:${color};background:radial-gradient(circle,${color} 0%,transparent 70%);animation-duration:${duration}s;animation-delay:-${delay}s;`;
        container.appendChild(el);
        el.addEventListener('animationiteration', () => {
            el.style.left = `${Math.random() * 100}%`;
            el.style.color = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
        });
    }
}

function createVirusParticles() {
    createBackgroundParticles('virus-particle', window.innerWidth < 768 ? 8 : 15, {
        minSize: 10,
        maxSize: 30,
        minDur: 20,
        maxDur: 35
    });
}

function createVirusCells() {
    const container = document.getElementById('virus-particles');
    if (!container) return;
    const colors = ['#ff00ff', '#00ffff', '#00ff88', '#aa00ff', '#ff0044'];
    for (let i = 0; i < 8; i++) {
        const cell = document.createElement('div');
        cell.className = 'virus-cell';
        const size = Math.random() * 30 + 20;
        const color = colors[Math.floor(Math.random() * colors.length)];
        const left = Math.random() * 100;
        const duration = Math.random() * 20 + 25;
        const delay = Math.random() * duration;
        const spikes = Math.floor(Math.random() * 4) + 6;
        const svgNs = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(svgNs, 'svg');
        svg.setAttribute('viewBox', '0 0 50 50');

        const mainCircle = document.createElementNS(svgNs, 'circle');
        mainCircle.setAttribute('cx', '25');
        mainCircle.setAttribute('cy', '25');
        mainCircle.setAttribute('r', '12');
        mainCircle.setAttribute('fill', color);
        mainCircle.setAttribute('opacity', '0.6');
        svg.appendChild(mainCircle);

        const innerCircle = document.createElementNS(svgNs, 'circle');
        innerCircle.setAttribute('cx', '25');
        innerCircle.setAttribute('cy', '25');
        innerCircle.setAttribute('r', '8');
        innerCircle.setAttribute('fill', color);
        svg.appendChild(innerCircle);

        for (let s = 0; s < spikes; s++) {
            const angle = (s / spikes) * Math.PI * 2;
            const line = document.createElementNS(svgNs, 'line');
            line.setAttribute('x1', 25 + Math.cos(angle) * 10);
            line.setAttribute('y1', 25 + Math.sin(angle) * 10);
            line.setAttribute('x2', 25 + Math.cos(angle) * 22);
            line.setAttribute('y2', 25 + Math.sin(angle) * 22);
            line.setAttribute('stroke', color);
            line.setAttribute('stroke-width', '3');
            line.setAttribute('stroke-linecap', 'round');
            svg.appendChild(line);

            const tip = document.createElementNS(svgNs, 'circle');
            tip.setAttribute('cx', 25 + Math.cos(angle) * 22);
            tip.setAttribute('cy', 25 + Math.sin(angle) * 22);
            tip.setAttribute('r', '3');
            tip.setAttribute('fill', color);
            svg.appendChild(tip);
        }

        cell.appendChild(svg);
        cell.style.cssText = `width:${size}px;height:${size}px;left:${left}%;color:${color};animation-duration:${duration}s;animation-delay:-${delay}s;`;
        container.appendChild(cell);
    }
}

function createGlowOrbs() {
    const container = document.getElementById('glow-orbs');
    if (!container) return;
    const orbs = [
        { color: 'rgba(255,0,128,0.3)', size: 300, x: '10%', y: '20%', delay: 0 },
        { color: 'rgba(0,255,255,0.25)', size: 250, x: '80%', y: '30%', delay: 2 },
        { color: 'rgba(170,0,255,0.2)', size: 200, x: '30%', y: '70%', delay: 4 },
        { color: 'rgba(0,255,136,0.2)', size: 180, x: '70%', y: '80%', delay: 6 },
        { color: 'rgba(255,136,0,0.15)', size: 150, x: '50%', y: '40%', delay: 3 }
    ];
    for (const orb of orbs) {
        const el = document.createElement('div');
        el.className = 'glow-orb';
        el.style.cssText = `width:${orb.size}px;height:${orb.size}px;left:${orb.x};top:${orb.y};background:${orb.color};animation-delay:-${orb.delay}s;`;
        container.appendChild(el);
    }
}

function createMatrixRain() {
    const container = document.getElementById('matrix-rain');
    if (!container) return;
    const chars =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()\u30A6\u30A8\u30AA\u30AB\u30AD\u30AF\u30B1\u30B3\u30B5\u30B7\u30B9\u30BB\u30BD\u30BF\u30C1\u30C4\u30C6\u30C8\u30CA\u30CB\u30CC\u30CD\u30CE\u30CF\u30D2\u30D5\u30D8\u30DB\u30DE\u30DF\u30E0\u30E1\u30E2\u30E4\u30E6\u30E8\u30E9\u30EA\u30EB\u30EC\u30ED\u30EF\u30F2\u30F3';
    const columnCount = Math.floor(window.innerWidth / 30);
    for (let i = 0; i < columnCount; i++) {
        const column = document.createElement('div');
        column.className = 'matrix-column';
        const length = Math.floor(Math.random() * 20) + 10;
        let text = '';
        for (let j = 0; j < length; j++) text += `${chars[Math.floor(Math.random() * chars.length)]}\n`;
        column.textContent = text;
        const left = (i / columnCount) * 100;
        const duration = Math.random() * 10 + 8;
        const delay = Math.random() * duration;
        column.style.cssText = `left:${left}%;animation-duration:${duration}s;animation-delay:-${delay}s;opacity:${Math.random() * 0.3 + 0.1};`;
        container.appendChild(column);
    }
}

function createBiohazardSymbols() {
    const container = document.getElementById('biohazard-container');
    if (!container) return;
    const svgNs = 'http://www.w3.org/2000/svg';
    const positions = [
        { x: '5%', y: '10%', size: 150, delay: 0 },
        { x: '85%', y: '60%', size: 120, delay: 5 },
        { x: '15%', y: '75%', size: 100, delay: 10 },
        { x: '75%', y: '15%', size: 80, delay: 15 }
    ];
    for (const pos of positions) {
        const symbol = document.createElement('div');
        symbol.className = 'biohazard-symbol';
        const svg = document.createElementNS(svgNs, 'svg');
        svg.setAttribute('viewBox', '0 0 100 100');
        const path = document.createElementNS(svgNs, 'path');
        path.setAttribute(
            'd',
            'M50 0C38.95 0 29.22 5.25 23.08 13.35C18.7 8.35 12.13 5.26 4.86 5.26C4.86 14.76 9.5 23.21 16.57 28.45C11.48 34.23 8.5 41.75 8.5 50C8.5 58.25 11.48 65.77 16.57 71.55C9.5 76.79 4.86 85.24 4.86 94.74C12.13 94.74 18.7 91.65 23.08 86.65C29.22 94.75 38.95 100 50 100C61.05 100 70.78 94.75 76.92 86.65C81.3 91.65 87.87 94.74 95.14 94.74C95.14 85.24 90.5 76.79 83.43 71.55C88.52 65.77 91.5 58.25 91.5 50C91.5 41.75 88.52 34.23 83.43 28.45C90.5 23.21 95.14 14.76 95.14 5.26C87.87 5.26 81.3 8.35 76.92 13.35C70.78 5.25 61.05 0 50 0ZM50 35C58.28 35 65 41.72 65 50C65 58.28 58.28 65 50 65C41.72 65 35 58.28 35 50C35 41.72 41.72 35 50 35Z'
        );
        svg.appendChild(path);
        symbol.appendChild(svg);
        symbol.style.cssText = `left:${pos.x};top:${pos.y};width:${pos.size}px;height:${pos.size}px;animation-delay:-${pos.delay}s;`;
        container.appendChild(symbol);
    }
}

// =========================================================================
// DYNAMIC EFFECTS
// =========================================================================

function createEnergyRing() {
    const colors = ['var(--neon-cyan)', 'var(--neon-pink)', 'var(--neon-purple)', 'var(--neon-green)'];
    const ring = document.createElement('div');
    ring.className = 'energy-ring';
    const x = Math.random() * window.innerWidth;
    const y = Math.random() * window.innerHeight;
    const size = Math.random() * 100 + 50;
    const color = colors[Math.floor(Math.random() * colors.length)];
    ring.style.cssText = `left:${x}px;top:${y}px;width:${size}px;height:${size}px;border-color:${color};box-shadow:0 0 20px ${color},inset 0 0 20px ${color};`;
    document.body.appendChild(ring);
    setTimeout(() => ring.remove(), 8000);
}

function triggerGlitch() {
    const overlay = document.getElementById('glitch-overlay');
    if (!overlay) return;
    overlay.classList.add('active');
    const lineCount = Math.floor(Math.random() * 3) + 1;
    for (let i = 0; i < lineCount; i++) {
        setTimeout(() => {
            const line = document.createElement('div');
            line.className = 'glitch-line';
            line.style.top = `${Math.random() * 100}%`;
            document.body.appendChild(line);
            setTimeout(() => line.remove(), 100);
        }, i * 50);
    }
    setTimeout(() => overlay.classList.remove('active'), 300);
}

function createDNAHelix() {
    const container = document.createElement('div');
    container.className = 'dna-container';
    container.style.cssText = `left:${Math.random() > 0.5 ? '5%' : '90%'};top:20%;width:60px;height:300px;`;
    const helix = document.createElement('div');
    helix.className = 'dna-helix';
    for (let i = 0; i < 20; i++) {
        const y = (i / 20) * 280;
        const angle = (i / 20) * Math.PI * 4;
        const x1 = Math.cos(angle) * 25 + 30;
        const x2 = Math.cos(angle + Math.PI) * 25 + 30;
        const node1 = document.createElement('div');
        node1.className = 'dna-node';
        node1.style.cssText = `left:${x1}px;top:${y}px;background:var(--neon-cyan);color:var(--neon-cyan);`;
        helix.appendChild(node1);
        const node2 = document.createElement('div');
        node2.className = 'dna-node';
        node2.style.cssText = `left:${x2}px;top:${y}px;background:var(--neon-pink);color:var(--neon-pink);`;
        helix.appendChild(node2);
        if (i % 2 === 0) {
            const link = document.createElement('div');
            link.className = 'dna-link';
            link.style.cssText = `left:${Math.min(x1, x2) + 4}px;top:${y + 3}px;width:${Math.abs(x2 - x1) - 8}px;`;
            helix.appendChild(link);
        }
    }
    container.appendChild(helix);
    document.body.appendChild(container);
    setTimeout(() => container.remove(), 30000);
}

function createLightning() {
    const svgNs = 'http://www.w3.org/2000/svg';
    const lightning = document.createElement('div');
    lightning.className = 'lightning';
    const x = Math.random() * window.innerWidth;
    const height = Math.random() * 300 + 200;
    let pathD = `M ${x} 0 `;
    let currentX = x;
    let currentY = 0;
    const segments = Math.floor(height / 30);
    for (let i = 0; i < segments; i++) {
        currentX += (Math.random() - 0.5) * 60;
        currentY += height / segments;
        pathD += `L ${currentX} ${currentY} `;
        if (Math.random() > 0.7) {
            const branchX = currentX + (Math.random() - 0.5) * 80;
            const branchY = currentY + Math.random() * 50;
            pathD += `M ${currentX} ${currentY} L ${branchX} ${branchY} M ${currentX} ${currentY} `;
        }
    }
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('width', window.innerWidth);
    svg.setAttribute('height', height + 100);
    svg.style.cssText = 'position:absolute;top:0;left:0;';
    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('d', pathD);
    path.setAttribute('stroke', 'var(--neon-cyan)');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('fill', 'none');
    svg.appendChild(path);
    lightning.appendChild(svg);
    document.body.appendChild(lightning);
    lightning.classList.add('flash');
    setTimeout(() => lightning.remove(), 300);
}

function initTypewriter() {
    const title = document.querySelector('.panel-header h1');
    if (!title || title.dataset.typewriter) return;
    title.dataset.typewriter = 'true';
    const text = title.textContent;
    title.textContent = '';
    title.classList.add('typewriter');
    let i = 0;
    const typeInterval = setInterval(() => {
        if (i < text.length) {
            title.textContent += text[i];
            i++;
        } else {
            clearInterval(typeInterval);
            setTimeout(() => title.classList.remove('typewriter'), 1000);
        }
    }, 100);
}

// =========================================================================
// EFFECT SCHEDULER
// =========================================================================

const effectScheduler = {
    intervals: {},
    isRunning: false,
    start() {
        if (this.isRunning) return;
        this.isRunning = true;
        this.scheduleRandom('glitch', triggerGlitch, 5000, 15000);
        this.scheduleRandom('lightning', createLightning, 8000, 20000);
        this.scheduleRandom('energyRing', createEnergyRing, 3000, 6000);
        this.scheduleRandom('dna', createDNAHelix, 30000, 60000);
    },
    scheduleRandom(name, fn, minDelay, maxDelay) {
        const schedule = () => {
            if (!this.isRunning) return;
            const delay = Math.random() * (maxDelay - minDelay) + minDelay;
            this.intervals[name] = setTimeout(() => {
                if (this.isRunning) {
                    fn();
                    schedule();
                }
            }, delay);
        };
        schedule();
    },
    stop() {
        this.isRunning = false;
        Object.values(this.intervals).forEach(clearTimeout);
        this.intervals = {};
    }
};

// =========================================================================
// MOUSE TRAIL + CLICK PARTICLES
// =========================================================================

function initMouseTrail() {
    if (!document.getElementById('sparkle-style')) {
        const style = document.createElement('style');
        style.id = 'sparkle-style';
        style.textContent = '@keyframes sparkleOut{0%{transform:scale(1);opacity:1}100%{transform:scale(0);opacity:0}}';
        document.head.appendChild(style);
    }

    document.addEventListener('mousemove', (e) => {
        if (!mouseTrailEnabled || !backgroundEffectsEnabled) return;
        const now = Date.now();
        if (now - lastTrailTime < 50) return;
        lastTrailTime = now;
        const sparkle = document.createElement('div');
        sparkle.style.cssText = `position:fixed;left:${e.clientX}px;top:${e.clientY}px;width:4px;height:4px;background:var(--neon-cyan);border-radius:50%;pointer-events:none;z-index:9999;box-shadow:0 0 10px var(--neon-cyan),0 0 20px var(--neon-cyan);animation:sparkleOut 0.6s ease-out forwards;`;
        document.body.appendChild(sparkle);
        setTimeout(() => sparkle.remove(), 600);
    });

    document.addEventListener('click', (e) => {
        if (!backgroundEffectsEnabled) return;
        const colors = ['#ff00ff', '#00ffff', '#00ff88', '#ffff00', '#ff8800', '#aa00ff'];
        for (let i = 0; i < 12; i++) {
            const particle = document.createElement('div');
            particle.className = 'click-particle';
            const angle = (i / 12) * Math.PI * 2;
            const velocity = 50 + Math.random() * 50;
            const tx = Math.cos(angle) * velocity;
            const ty = Math.sin(angle) * velocity;
            const color = colors[Math.floor(Math.random() * colors.length)];
            particle.style.cssText = `left:${e.clientX}px;top:${e.clientY}px;background:${color};box-shadow:0 0 10px ${color},0 0 20px ${color};--tx:${tx}px;--ty:${ty}px;`;
            document.body.appendChild(particle);
            setTimeout(() => particle.remove(), 800);
        }
    });
}

// =========================================================================
// PUBLIC API
// =========================================================================

export function initAllEffects() {
    createVirusParticles();
    createVirusCells();
    createGlowOrbs();
    createMatrixRain();
    createBiohazardSymbols();
    createEnergyRing();
    createDNAHelix();
    initMouseTrail();
    energyRingInterval = setInterval(createEnergyRing, 3000);
    effectScheduler.start();
    setTimeout(initTypewriter, 500);
}

export function disableBackgroundEffects() {
    backgroundEffectsEnabled = false;
    if (energyRingInterval) clearInterval(energyRingInterval);
    effectScheduler.stop();
    ['virus-particles', 'glow-orbs', 'matrix-rain', 'biohazard-container'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.style.opacity = '0';
    });
    const scanlines = document.querySelector('.scanlines');
    if (scanlines) scanlines.style.opacity = '0';
    document.querySelectorAll('.dna-container, .lightning, .energy-ring').forEach((el) => el.remove());
    mouseTrailEnabled = false;
}

export function enableBackgroundEffects() {
    backgroundEffectsEnabled = true;
    ['virus-particles', 'glow-orbs', 'matrix-rain', 'biohazard-container'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
            el.style.transition = 'opacity 1s ease';
            el.style.opacity = '1';
        }
    });
    const scanlines = document.querySelector('.scanlines');
    if (scanlines) {
        scanlines.style.transition = 'opacity 1s ease';
        scanlines.style.opacity = '1';
    }
    effectScheduler.start();
    mouseTrailEnabled = true;
    if (!energyRingInterval) {
        energyRingInterval = setInterval(createEnergyRing, 3000);
    }
}

export function createExplosionEffect(x, y) {
    const explosion = document.createElement('div');
    explosion.className = 'explosion-effect';
    explosion.style.left = `${x - 50}px`;
    explosion.style.top = `${y - 50}px`;
    document.body.appendChild(explosion);
    const shockwave = document.createElement('div');
    shockwave.className = 'shockwave';
    shockwave.style.left = `${x}px`;
    shockwave.style.top = `${y}px`;
    document.body.appendChild(shockwave);
    setTimeout(() => {
        explosion.remove();
        shockwave.remove();
    }, 1000);
}

export { backgroundEffectsEnabled };
